import { useEffect, useState } from 'react';

import { decodeJwt } from '../utils/jwt';
import { tokenStorage } from '../utils/tokenStorage';

export interface SessionExpiryState {
  /** Seconds until the access token expires. `null` if no token / no `exp`. */
  secondsRemaining: number | null;
  /** `true` once within the warning window. */
  warning: boolean;
  /** `true` once the token has expired (secondsRemaining <= 0). */
  expired: boolean;
}

export interface UseSessionExpiryOptions {
  /**
   * How many seconds before `exp` to flip `warning` to `true`. Default: 60.
   */
  warnSeconds?: number;
  /** Polling interval in ms. Default: 5000. */
  pollMs?: number;
}

/**
 * Tracks the remaining lifetime of the stored access token. Components
 * consuming this hook can render a "your session is about to expire" banner
 * or modal and call the refresh endpoint on the user's behalf.
 */
export function useSessionExpiry(options: UseSessionExpiryOptions = {}): SessionExpiryState {
  const { warnSeconds = 60, pollMs = 5000 } = options;
  const [state, setState] = useState<SessionExpiryState>({
    secondsRemaining: null,
    warning: false,
    expired: false,
  });

  useEffect(() => {
    const tick = () => {
      const token = tokenStorage.getAccessToken();
      if (!token) {
        setState({ secondsRemaining: null, warning: false, expired: false });
        return;
      }
      const payload = decodeJwt(token);
      if (!payload || typeof payload.exp !== 'number') {
        setState({ secondsRemaining: null, warning: false, expired: false });
        return;
      }
      const remaining = payload.exp - Math.floor(Date.now() / 1000);
      setState({
        secondsRemaining: remaining,
        warning: remaining > 0 && remaining <= warnSeconds,
        expired: remaining <= 0,
      });
    };

    tick();
    const id = window.setInterval(tick, pollMs);
    return () => {
      window.clearInterval(id);
    };
  }, [warnSeconds, pollMs]);

  return state;
}
